/// <reference types="@types/google.maps"/>

import { Injectable, NgZone } from '@angular/core';
import loadjs from 'loadjs';
import { combineLatest, from, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { DocumentRef } from './browser-globals';
import { MapsConfig } from './maps-config';

@Injectable()
export class GoogleMaps {
  private _google$!: Observable<typeof google>;
  private _geocoder!: google.maps.Geocoder;
  private _autocompleteService!: google.maps.places.AutocompleteService;
  private _placesService!: google.maps.places.PlacesService;
  private _mapsScriptId = 'maps-api-script';

  public constructor(
    private config: MapsConfig,
    private zone: NgZone,
    private documentRef: DocumentRef
  ) {}

  public autocomplete(request: google.maps.places.AutocompletionRequest) {
    return combineLatest([this.google$, this.autocompleteService$]).pipe(
      switchMap(
        ([g, autocomplete]) =>
          new Observable<google.maps.places.AutocompletePrediction[]>((observer) => {
            autocomplete.getPlacePredictions(request, (results, status) => {
              this.zone.run(() => {
                if (
                  status === g.maps.places.PlacesServiceStatus.OK ||
                  status === g.maps.places.PlacesServiceStatus.ZERO_RESULTS
                ) {
                  observer.next(results || []);
                  observer.complete();
                } else {
                  observer.error(status);
                  observer.complete();
                }
              });
            });
          })
      )
    );
  }

  public fetchAddressFromLatLng(lat: number, lng: number) {
    return this.google$.pipe(
      switchMap((google) => {
        const latLng = new google.maps.LatLng(lat, lng);
        return this.geocode({ location: latLng });
      }),
      filter((data) => !!data.length),
      map((data) => data[0].formatted_address)
    );
  }

  public geocode(request: google.maps.GeocoderRequest) {
    return combineLatest([this.google$, this.geocoder$]).pipe(
      switchMap(
        ([g, geocoder]) =>
          new Observable<google.maps.GeocoderResult[]>((observer) => {
            geocoder.geocode(request, (results, status) => {
              if (
                status === g.maps.GeocoderStatus.OK ||
                status === g.maps.GeocoderStatus.ZERO_RESULTS
              ) {
                observer.next(results || []);
                observer.complete();
              } else {
                observer.error(status);
                observer.complete();
              }
            });
          })
      )
    );
  }

  public getPlace(placeId: string) {
    return combineLatest([this.google$, this.placesService$]).pipe(
      switchMap(
        ([g, placesService]) =>
          new Observable<google.maps.places.PlaceResult | null>((observer) => {
            placesService.getDetails({ placeId }, (result, status) => {
              if (
                status === g.maps.places.PlacesServiceStatus.OK ||
                status === g.maps.places.PlacesServiceStatus.ZERO_RESULTS
              ) {
                observer.next(result);
                observer.complete();
              } else {
                observer.error(status);
                observer.complete();
              }
            });
          })
      )
    );
  }

  private get google$() {
    if (!loadjs.isDefined(this._mapsScriptId)) {
      this._google$ = this.loadGoogleMaps();
    }

    return this._google$;
  }

  private loadGoogleMaps() {
    return from(
      loadjs(
        `https://maps.googleapis.com/maps/api/js?v=3&key=${this.config.apiKey}&libraries=places`,
        this._mapsScriptId,
        {
          returnPromise: true,
        }
      ).then(() => window.google)
    );
  }

  private get geocoder$() {
    if (this._geocoder) {
      return of(this._geocoder);
    }

    return this.google$.pipe(
      map((g) => {
        this._geocoder = new g.maps.Geocoder();
        return this._geocoder;
      })
    );
  }

  private get autocompleteService$() {
    if (this._autocompleteService) {
      return of(this._autocompleteService);
    }

    return this.google$.pipe(
      map((g) => {
        this._autocompleteService = new g.maps.places.AutocompleteService();
        return this._autocompleteService;
      })
    );
  }

  private get placesService$() {
    if (this._placesService) {
      return of(this._placesService);
    }

    return this.google$.pipe(
      map((g) => {
        this._placesService = new g.maps.places.PlacesService(
          this.documentRef.getNativeDocument().createElement('div')
        );
        return this._placesService;
      })
    );
  }
}
